<script setup>
// KISOFT: editar TODOS os dados do contato num modal: nome, e-mail, telefone, IDENTIFIER, empresa,
// cidade/pais, bio, redes sociais, foto e TODOS os atributos personalizados (inclusive chaves sem
// definicao). Abre pelo botao "Editar contato" no topo da conversa e pelo lapis do painel do contato.
// Salva pela action nativa contacts/update: atributos personalizados vao em `customAttributes`
// (a action NAO converte essas chaves p/ snake_case; o resto ela converte).
import { ref, computed, nextTick, onMounted, onBeforeUnmount } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { frontendURL } from 'dashboard/helper/URLHelper';
import MessageApi from 'dashboard/api/inbox/message';
import MessageFormatter from 'shared/helpers/MessageFormatter';
import ContactAPI from 'dashboard/api/contacts';
import ContactMergeModal from 'dashboard/modules/contact/ContactMergeModal.vue';
import countries from 'shared/constants/countries.js';
import Avatar from 'next/avatar/Avatar.vue';

const props = defineProps({ contactId: { type: [Number, String], required: true } });
const emit = defineEmits(['close']);
const store = useStore();
const router = useRouter();

const contact = computed(() => store.getters['contacts/getContact'](Number(props.contactId)) || {});
const defs = computed(() => store.getters['attributes/getAttributesByModel']('contact_attribute') || []);
const defMap = computed(() => Object.fromEntries(defs.value.map(d => [d.attribute_key, d])));
// chaves que o contato tem mas que nao tem definicao cadastrada -> tambem editaveis (texto)
const orfas = computed(() => Object.keys(custom.value).filter(k => !defMap.value[k]));

const SOCIAL = [['facebook', 'Facebook'], ['instagram', 'Instagram'], ['linkedin', 'LinkedIn'], ['twitter', 'X / Twitter'], ['github', 'GitHub']];
const NUM = ['number', 'currency', 'percent'];
const INP = 'w-full h-9 px-3 rounded-lg bg-n-alpha-black2 outline outline-1 outline-n-weak text-sm text-n-slate-12 placeholder:text-n-slate-10 focus:outline-n-brand';
const LBL = 'block mb-1 text-xs font-medium text-n-slate-11';

const form = ref(null);
const custom = ref({});
let chavesOrig = new Set();   // atributos que o contato JA tinha (so esses + os preenchidos agora sao enviados)
const carregando = ref(true);
const salvando = ref(false);
const fotoBusy = ref(false);
const erro = ref('');
const arquivo = ref(null);

const preencher = () => {
  const c = contact.value;
  const a = c.additional_attributes || {};
  const sp = a.social_profiles || {};
  let pais = a.country_code || '';
  if (!pais && a.country) { const ach = countries.find(x => x.name.toLowerCase() === String(a.country).toLowerCase()); if (ach) pais = ach.id; }
  form.value = {
    name: c.name || '', email: c.email || '', phone: c.phone_number || '', identifier: c.identifier || '',
    company: a.company_name || '', city: a.city || '', country: pais, bio: a.description || '',
    social: Object.fromEntries(SOCIAL.map(([k]) => [k, sp[k] || ''])),
  };
  const cv = { ...(c.custom_attributes || {}) };
  chavesOrig = new Set(Object.keys(cv));
  defs.value.forEach(d => {
    const k = d.attribute_key;
    if (d.attribute_display_type === 'date' && cv[k]) cv[k] = String(cv[k]).slice(0, 10);   // ISO -> YYYY-MM-DD
    if (d.attribute_display_type === 'checkbox') cv[k] = cv[k] === true || cv[k] === 'true';
    else if (!(k in cv)) cv[k] = '';
  });
  custom.value = cv;
};

onMounted(async () => {
  try {
    // conversas do contato: uma aba pra cada (o historico de cada uma so carrega ao abrir a aba)
    const pedidos = [
      store.dispatch('contacts/show', { id: Number(props.contactId) }),
      store.dispatch('contactConversations/get', Number(props.contactId)),
    ];
    if (!defs.value.length) pedidos.push(store.dispatch('attributes/get'));
    await Promise.all(pedidos);
  } catch (e) {
    // sem rede: segue com o que ja esta no store
  }
  preencher();
  carregando.value = false;
  window.addEventListener('keydown', teclas);
});
onBeforeUnmount(() => window.removeEventListener('keydown', teclas));

const fechar = () => { if (!salvando.value) emit('close'); };
function teclas(e) {
  if (mergeAberto.value) return;
  if (e.key === 'Escape') fechar();
  if (e.key === 'Enter' && (e.ctrlKey || e.metaKey) && aba.value === 'dados') salvar();
}

const salvar = async () => {
  if (!form.value || salvando.value) return;
  erro.value = '';
  const f = form.value;
  const fone = f.phone.replace(/[\s().-]/g, '');
  if (!f.name.trim()) { erro.value = 'O nome não pode ficar vazio.'; return; }
  if (f.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email.trim())) { erro.value = 'E-mail inválido.'; return; }
  if (fone && !/^\+[1-9]\d{6,14}$/.test(fone)) { erro.value = 'Telefone no formato internacional: + código do país + DDD + número. Ex.: +5521999998888'; return; }
  const cust = {};
  for (const k of Object.keys(custom.value)) {
    let v = custom.value[k];
    const d = defMap.value[k];
    if (typeof v === 'string') v = v.trim();
    // atributo que o contato nao tinha e continua vazio: nao grava (nao polui o contato com chaves nulas)
    if (!chavesOrig.has(k) && (v === '' || v === null || v === false)) continue;
    if (d && d.regex_pattern && v !== '' && v !== null && typeof v === 'string') {
      try { if (!new RegExp(d.regex_pattern).test(v)) { erro.value = (d.attribute_display_name || k) + ': ' + (d.regex_cue || 'valor fora do formato.'); return; } } catch (e) { /* regex invalida: ignora */ }
    }
    if (d && d.attribute_display_type === 'date' && v) v = new Date(v + 'T00:00:00').toISOString();
    if (d && NUM.includes(d.attribute_display_type) && v !== '' && v !== null) v = Number(v);
    cust[k] = v === '' ? null : v;
  }
  const pais = countries.find(x => x.id === f.country);
  const a = contact.value.additional_attributes || {};
  const social = {};
  SOCIAL.forEach(([k]) => { social[k] = (f.social[k] || '').trim(); });
  salvando.value = true;
  try {
    await store.dispatch('contacts/update', {
      id: contact.value.id,
      name: f.name.trim(),
      email: f.email.trim() || null,
      phone_number: fone || null,
      identifier: f.identifier.trim() || null,
      additional_attributes: {
        ...a,
        company_name: f.company.trim(),
        city: f.city.trim(),
        country: pais ? pais.name : '',
        country_code: pais ? pais.id : '',
        description: f.bio,
        social_profiles: { ...(a.social_profiles || {}), ...social },
      },
      customAttributes: cust,
    });
    useAlert('Contato atualizado.');
    emit('close');
  } catch (e) {
    erro.value = (e && e.message) || 'Não foi possível salvar o contato.';
  } finally {
    salvando.value = false;
  }
};

const trocarFoto = async ev => {
  const arq = ev.target.files && ev.target.files[0];
  ev.target.value = '';
  if (!arq) return;
  fotoBusy.value = true;
  try {
    await store.dispatch('contacts/update', { id: contact.value.id, isFormData: true, avatar: arq });
    useAlert('Foto atualizada.');
  } catch (e) {
    useAlert((e && e.message) || 'Não foi possível trocar a foto.');
  } finally { fotoBusy.value = false; }
};
const removerFoto = async () => {
  fotoBusy.value = true;
  try { await store.dispatch('contacts/deleteAvatar', contact.value.id); useAlert('Foto removida.'); }
  catch (e) { useAlert('Não foi possível remover a foto.'); }
  finally { fotoBusy.value = false; }
};
const rotulo = k => (defMap.value[k] && defMap.value[k].attribute_display_name) || k;

// ===== KISOFT: mesclar contato (mesma janela da pagina de Contatos) =====
const mergeRef = ref(null);
const mergeAberto = ref(false);
const abrirMerge = () => {
  if (!contact.value.id) return;
  mergeAberto.value = true;
  nextTick(() => mergeRef.value && mergeRef.value.open());
};
// ao mesclar, ESTE contato (o filho) deixa de existir -> nao da pra continuar editando: fecha o modal.
// Se o usuario so cancelou, o contato ainda existe e a gente recarrega os dados.
const fecharMerge = async () => {
  mergeAberto.value = false;
  try {
    await ContactAPI.show(Number(props.contactId));
    await store.dispatch('contacts/show', { id: Number(props.contactId) });
    preencher();
    store.dispatch('contactConversations/get', Number(props.contactId)).catch(() => {});
  } catch (e) {
    emit('close');
  }
};

// ===== KISOFT: aba "Conversas" — todas as conversas do contato (store contactConversations) =====
// UMA ABA POR CONVERSA (alem da aba "dados"): selecionar a aba carrega o historico dela aqui dentro.
const aba = ref('dados');   // 'dados' | id da conversa
const conversas = computed(() => store.getters['contactConversations/getContactConversation'](Number(props.contactId)) || []);
const convFlags = computed(() => store.getters['contactConversations/getUIFlags'] || {});
const accountId = computed(() => store.getters.getCurrentAccountId);
const convAtual = computed(() => conversas.value.find(c => String(c.id) === String(aba.value)) || null);
// mensagens por conversa: busca na 1a vez que a aba e aberta e fica em cache enquanto o modal estiver aberto
const msgs = ref({});
const msgBusy = ref({});
const msgErro = ref({});
const carregarMsgs = async (id, recarregar = false) => {
  if (!recarregar && (msgs.value[id] || msgBusy.value[id])) return;
  msgBusy.value = { ...msgBusy.value, [id]: true };
  msgErro.value = { ...msgErro.value, [id]: '' };
  try {
    const { data } = await MessageApi.getPreviousMessages({ conversationId: id });
    msgs.value = { ...msgs.value, [id]: (data && data.payload) || [] };
  } catch (e) {
    msgErro.value = { ...msgErro.value, [id]: 'Não foi possível carregar as mensagens.' };
  } finally {
    msgBusy.value = { ...msgBusy.value, [id]: false };
  }
};
const abrirAba = a => {
  aba.value = a;
  if (a !== 'dados') carregarMsgs(Number(a));
};
const abaCls = a =>
  'shrink-0 h-9 px-3 -mb-px border-b-2 text-sm font-medium whitespace-nowrap ' +
  (String(aba.value) === String(a) ? 'border-n-brand text-n-slate-12' : 'border-transparent text-n-slate-11 hover:text-n-slate-12');

const ST = {
  open: ['Aberta', 'text-n-teal-11 bg-n-teal-3'],
  pending: ['Pendente', 'text-n-amber-11 bg-n-amber-3'],
  snoozed: ['Adiada', 'text-n-slate-11 bg-n-alpha-2'],
  resolved: ['Resolvida', 'text-n-slate-11 bg-n-alpha-2'],
};
const stRot = c => (ST[c.status] || [c.status || '—', 'text-n-slate-11 bg-n-alpha-2'])[0];
const stCls = c => (ST[c.status] || ['', 'text-n-slate-11 bg-n-alpha-2'])[1];
const caixa = c => {
  const i = store.getters['inboxes/getInbox'](c.inbox_id);
  return (i && i.name) || 'Caixa de entrada';
};
const responsavel = c => (c.meta && c.meta.assignee && c.meta.assignee.name) || 'Sem responsável';
const quando = c => {
  const ts = c.timestamp || c.last_activity_at || 0;
  if (!ts) return '';
  const d = new Date(ts * 1000);
  const hoje = new Date();
  const mesmoDia = d.toDateString() === hoje.toDateString();
  return mesmoDia
    ? 'hoje ' + d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
    : d.toLocaleDateString('pt-BR') + ' ' + d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
};
const abrirConversa = (c, e) => {
  const path = frontendURL(`accounts/${accountId.value}/conversations/${c.id}`);
  if (e && (e.metaKey || e.ctrlKey)) {
    window.open(`${window.chatwootConfig.hostURL}${path}`, '_blank', 'noopener,noreferrer');
    return;
  }
  emit('close');
  router.push({ path });
};

// rotulo curto da aba: caixa + #id (o status vira a bolinha colorida)
const abaRot = c => {
  const n = caixa(c);
  const curto = n.length > 18 ? n.slice(0, 18) + '…' : n;
  // mais de uma conversa na mesma caixa: desempata pela data (nunca pelo numero da conversa)
  const repetida = conversas.value.filter(x => x.inbox_id === c.inbox_id).length > 1;
  if (!repetida) return curto;
  const ts = c.timestamp || c.last_activity_at || 0;
  return ts ? curto + ' · ' + new Date(ts * 1000).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }) : curto;
};
const stDot = c =>
  ({ open: 'bg-n-teal-9', pending: 'bg-n-amber-9', snoozed: 'bg-n-slate-9', resolved: 'bg-n-slate-8' }[c.status] || 'bg-n-slate-8');

// --- mensagens da conversa selecionada ---
const msgTipo = m => (m.private ? 'nota' : { 0: 'in', 1: 'out', 3: 'out' }[m.message_type] || 'act');
const msgTexto = m =>
  (m.content || '')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .trim();
const msgHora = m =>
  m.created_at
    ? new Date(m.created_at * 1000).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
    : '';
const msgHtml = m => new MessageFormatter(m.content || '', false, !!m.private).formattedMessage;
const msgAutor = m => (m.sender && (m.sender.name || m.sender.available_name)) || '';
const anexos = m => m.attachments || [];
</script>

<template>
  <Teleport to="body">
    <!-- altura presa na tela: cabecalho/rodape fixos, so o meio rola; largo (2 colunas) p/ quase nunca precisar rolar -->
    <div class="fixed inset-0 z-[300] flex items-center justify-center bg-black/40 p-3 sm:p-6" @mousedown.self="fechar">
      <div class="flex flex-col w-full max-w-6xl h-[calc(100vh-1.5rem)] sm:h-[min(52rem,calc(100vh-3rem))] rounded-2xl bg-n-solid-1 outline outline-1 outline-n-weak shadow-2xl">
        <div class="flex shrink-0 items-center gap-3 px-6 py-4 border-b border-n-weak">
          <div class="min-w-0 flex-1">
            <h2 class="text-base font-semibold text-n-slate-12">Editar contato</h2>
            <p class="text-xs text-n-slate-11 truncate">Todos os dados de {{ contact.name || 'contato' }} · #{{ contact.id }}</p>
          </div>
          <button
            type="button"
            class="shrink-0 h-8 px-3 rounded-lg text-xs font-medium text-n-slate-12 outline outline-1 outline-n-weak hover:bg-n-alpha-2 disabled:opacity-50"
            title="Mesclar este contato com outro"
            :disabled="!contact.id"
            @click="abrirMerge"
          >
            Mesclar contato
          </button>
          <button type="button" class="i-lucide-x size-5 text-n-slate-11 hover:text-n-slate-12" title="Fechar (Esc)" @click="fechar" />
        </div>

        <!-- abas: "Dados" + UMA ABA POR CONVERSA do contato -->
        <div class="flex shrink-0 items-center gap-2 px-6 overflow-x-auto border-b border-n-weak">
          <button type="button" :class="abaCls('dados')" @click="abrirAba('dados')">Dados</button>
          <button
            v-for="c in conversas"
            :key="c.id"
            type="button"
            :class="abaCls(c.id)"
            :title="caixa(c) + ' · ' + stRot(c) + ' · ' + responsavel(c)"
            @click="abrirAba(c.id)"
          >
            <span class="inline-block size-2 rounded-full align-middle mr-1.5" :class="stDot(c)" />{{ abaRot(c) }}
            <span v-if="c.unread_count" class="ml-1 rounded-full bg-n-ruby-9 px-1.5 py-0.5 text-[11px] font-semibold text-white">{{ c.unread_count }}</span>
          </button>
          <span v-if="convFlags.isFetching" class="shrink-0 text-xs text-n-slate-10">carregando conversas…</span>
          <span v-else-if="!conversas.length" class="shrink-0 text-xs text-n-slate-10">sem conversas</span>
        </div>

        <!-- conteudo da aba de conversa: historico carregado aqui dentro -->
        <div v-if="convAtual" class="flex flex-1 min-h-0 flex-col">
          <div class="flex shrink-0 items-center gap-2 px-6 py-2 border-b border-n-weak bg-n-alpha-1">
            <span class="rounded-full px-2 py-0.5 text-[11px] font-medium" :class="stCls(convAtual)">{{ stRot(convAtual) }}</span>
            <span class="truncate text-xs text-n-slate-11">{{ caixa(convAtual) }} · {{ responsavel(convAtual) }} · {{ quando(convAtual) }}</span>
            <button type="button" class="ml-auto shrink-0 text-xs font-medium text-n-brand hover:underline" @click="carregarMsgs(convAtual.id, true)">Atualizar</button>
            <button type="button" class="shrink-0 text-xs font-medium text-n-brand hover:underline" @click="abrirConversa(convAtual, $event)">Abrir conversa ↗</button>
          </div>
          <div class="flex-1 min-h-0 overflow-y-auto px-6 py-4">
            <p v-if="msgBusy[convAtual.id]" class="py-12 text-center text-sm text-n-slate-11">Carregando a conversa…</p>
            <p v-else-if="msgErro[convAtual.id]" class="py-12 text-center text-sm text-n-ruby-11">{{ msgErro[convAtual.id] }}</p>
            <p v-else-if="!(msgs[convAtual.id] || []).length" class="py-12 text-center text-sm text-n-slate-11">Nenhuma mensagem nesta conversa.</p>
            <div v-else class="flex flex-col gap-2">
              <div v-for="m in msgs[convAtual.id]" :key="m.id" class="flex" :class="msgTipo(m) === 'out' ? 'justify-end' : msgTipo(m) === 'act' ? 'justify-center' : 'justify-start'">
                <p v-if="msgTipo(m) === 'act'" class="px-3 py-1 text-[11px] text-n-slate-10">{{ msgTexto(m) }}</p>
                <div
                  v-else
                  class="max-w-[75%] rounded-xl px-3 py-2"
                  :class="msgTipo(m) === 'nota' ? 'bg-n-amber-3 outline outline-1 outline-n-amber-5' : msgTipo(m) === 'out' ? 'bg-n-brand/10 outline outline-1 outline-n-brand/20' : 'bg-n-alpha-2'"
                >
                  <p class="mb-0.5 text-[11px] text-n-slate-10">
                    <span v-if="msgTipo(m) === 'nota'" class="font-semibold text-n-amber-11">Nota interna · </span>{{ msgAutor(m) }}<span v-if="msgAutor(m)"> · </span>{{ msgHora(m) }}
                  </p>
                  <div v-if="msgTexto(m)" class="kis-md break-words text-sm text-n-slate-12" v-html="msgHtml(m)" />
                  <div v-if="anexos(m).length" class="mt-1 flex flex-wrap gap-2">
                    <a v-for="at in anexos(m)" :key="at.id" :href="at.data_url" target="_blank" rel="noopener noreferrer" class="text-xs text-n-brand hover:underline">
                      <img v-if="at.file_type === 'image'" :src="at.thumb_url || at.data_url" class="max-h-40 rounded-lg" alt="anexo">
                      <span v-else>📎 {{ at.file_type || 'anexo' }}</span>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-else-if="carregando || !form" class="flex-1 min-h-0 px-6 py-16 text-center text-sm text-n-slate-11">Carregando o contato…</div>

        <div v-else class="flex-1 min-h-0 overflow-y-auto px-6 py-5">
         <div class="grid grid-cols-1 gap-x-8 gap-y-6 lg:grid-cols-2">
          <div class="space-y-6">
          <!-- foto + identificacao -->
          <section class="flex flex-col gap-5 sm:flex-row">
            <div class="flex flex-col items-center gap-2 sm:w-32 shrink-0">
              <Avatar :name="contact.name || '?'" :src="contact.thumbnail || ''" :size="72" rounded-full />
              <label class="text-xs font-medium text-n-brand cursor-pointer hover:underline" :class="{ 'opacity-50 pointer-events-none': fotoBusy }">
                {{ fotoBusy ? 'Enviando…' : 'Trocar foto' }}
                <input ref="arquivo" type="file" accept="image/png,image/jpeg,image/gif,image/webp" class="hidden" @change="trocarFoto">
              </label>
              <button v-if="contact.thumbnail" type="button" class="text-xs text-n-slate-11 hover:text-n-ruby-11" :disabled="fotoBusy" @click="removerFoto">Remover foto</button>
            </div>
            <div class="grid flex-1 grid-cols-1 gap-3 sm:grid-cols-2">
              <div class="sm:col-span-2"><label :class="LBL">Nome</label><input v-model="form.name" :class="INP" placeholder="Nome do contato"></div>
              <div><label :class="LBL">E-mail</label><input v-model="form.email" type="email" :class="INP" placeholder="email@exemplo.com"></div>
              <div><label :class="LBL">Telefone</label><input v-model="form.phone" :class="INP" placeholder="+5521999998888"></div>
              <div class="sm:col-span-2">
                <label :class="LBL">Identifier</label>
                <input v-model="form.identifier" :class="INP + ' font-mono'" placeholder="(vazio)">
                <p class="mt-1 text-[11px] leading-snug text-n-amber-11">Usado pelas integrações: no Telegram é o ID do Telegram e no WhatsApp pode ser o <span class="font-mono">@lid</span>. Mudar ou apagar pode fazer a próxima mensagem desse canal cair num contato novo.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 class="mb-2 text-[11px] font-semibold uppercase tracking-wide text-n-slate-10">Perfil</h3>
            <div class="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div><label :class="LBL">Empresa</label><input v-model="form.company" :class="INP"></div>
              <div><label :class="LBL">Cidade</label><input v-model="form.city" :class="INP"></div>
              <div>
                <label :class="LBL">País</label>
                <select v-model="form.country" :class="INP">
                  <option value="">—</option>
                  <option v-for="c in countries" :key="c.id" :value="c.id">{{ c.emoji }} {{ c.name }}</option>
                </select>
              </div>
              <div class="sm:col-span-3"><label :class="LBL">Bio / observações</label><textarea v-model="form.bio" rows="2" :class="INP + ' h-auto py-2 resize-y'" /></div>
            </div>
          </section>
          </div>

          <div class="space-y-6">
          <section>
            <h3 class="mb-2 text-[11px] font-semibold uppercase tracking-wide text-n-slate-10">Redes sociais</h3>
            <div class="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div v-for="[k, lb] in SOCIAL" :key="k"><label :class="LBL">{{ lb }}</label><input v-model="form.social[k]" :class="INP" placeholder="usuário"></div>
            </div>
          </section>

          <section>
            <h3 class="mb-2 text-[11px] font-semibold uppercase tracking-wide text-n-slate-10">Atributos personalizados</h3>
            <div v-if="!defs.length && !orfas.length" class="text-xs text-n-slate-11">Nenhum atributo personalizado de contato cadastrado.</div>
            <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div v-for="d in defs" :key="d.attribute_key">
                <label :class="LBL">{{ d.attribute_display_name }}</label>
                <select v-if="d.attribute_display_type === 'list'" v-model="custom[d.attribute_key]" :class="INP">
                  <option value="">—</option>
                  <option v-for="op in (d.attribute_values || [])" :key="op" :value="op">{{ op }}</option>
                </select>
                <label v-else-if="d.attribute_display_type === 'checkbox'" class="flex items-center gap-2 h-9 text-sm text-n-slate-12">
                  <input v-model="custom[d.attribute_key]" type="checkbox" class="size-4"> Sim
                </label>
                <input v-else-if="d.attribute_display_type === 'date'" v-model="custom[d.attribute_key]" type="date" :class="INP">
                <input v-else-if="NUM.includes(d.attribute_display_type)" v-model="custom[d.attribute_key]" type="number" step="any" :class="INP">
                <input v-else v-model="custom[d.attribute_key]" :type="d.attribute_display_type === 'link' ? 'url' : 'text'" :class="INP">
              </div>
              <div v-for="k in orfas" :key="'o-' + k">
                <label :class="LBL">{{ rotulo(k) }} <span class="font-normal text-n-slate-10">(sem definição)</span></label>
                <input v-model="custom[k]" :class="INP">
              </div>
            </div>
          </section>
          </div>
         </div>
        </div>

        <div class="flex shrink-0 items-center gap-3 px-6 py-4 border-t border-n-weak">
          <p class="flex-1 min-w-0 text-xs" :class="erro ? 'text-n-ruby-11 font-medium' : 'text-n-slate-10'">
            {{ erro || (aba !== 'dados' ? 'Histórico desta conversa · Esc fecha' : 'Ctrl+Enter salva · Esc fecha') }}
          </p>
          <button type="button" class="h-9 px-4 rounded-lg text-sm font-medium text-n-slate-12 outline outline-1 outline-n-weak hover:bg-n-alpha-2" @click="fechar">{{ aba !== 'dados' ? 'Fechar' : 'Cancelar' }}</button>
          <button v-if="aba === 'dados'" type="button" class="h-9 px-4 rounded-lg text-sm font-semibold text-white bg-n-brand hover:opacity-90 disabled:opacity-50" :disabled="salvando || carregando" @click="salvar">{{ salvando ? 'Salvando…' : 'Salvar' }}</button>
        </div>
      </div>
      <ContactMergeModal v-if="mergeAberto && contact.id" ref="mergeRef" :primary-contact="contact" @close="fecharMerge" />
    </div>
  </Teleport>
</template>

<style>
/* KISOFT: markdown das mensagens (o formatador do Chatwoot devolve HTML) */
.kis-md p { margin: 0 0 .35rem; }
.kis-md p:last-child { margin-bottom: 0; }
.kis-md strong, .kis-md b { font-weight: 700; }
.kis-md em { font-style: italic; }
.kis-md del, .kis-md s { text-decoration: line-through; }
.kis-md a { text-decoration: underline; word-break: break-all; }
.kis-md code { background: rgba(127,127,127,.18); border-radius: 4px; padding: 0 .25rem; font-size: .92em; font-family: ui-monospace, SFMono-Regular, Menlo, monospace; }
.kis-md pre { background: rgba(127,127,127,.18); border-radius: 8px; padding: .5rem .6rem; overflow-x: auto; }
.kis-md pre code { background: none; padding: 0; }
.kis-md ul, .kis-md ol { margin: .25rem 0 .35rem 1.1rem; list-style: revert; }
.kis-md blockquote { border-left: 2px solid rgba(127,127,127,.4); padding-left: .5rem; margin: .25rem 0; }
.kis-md img { max-width: 100%; border-radius: 8px; }
</style>
